import React, { useState, useCallback } from 'react';
import { TextField, InputAdornment, Typography, Box, LinearProgress } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import WarningIcon from '@material-ui/icons/Warning';
import debounce from 'lodash.debounce';
import { transceive } from './actions';

/**
 * Real-time PIN validation component
 * Provides instant feedback on PIN format and strength
 * Checks remaining retry attempts without consuming them
 */

const PINValidator = ({
  value,
  onChange,
  label = 'PIN',
  type = 'PIV', // 'PIV' or 'OpenPGP'
  dispatch,
  helperText,
  ...textFieldProps
}) => {
  const [validation, setValidation] = useState({
    valid: false,
    error: null,
    warning: null,
    retriesLeft: null,
    strength: null
  });
  const [checking, setChecking] = useState(false);

  // PIN format requirements
  const PIN_REQUIREMENTS = {
    PIV: {
      minLength: 6,
      maxLength: 8,
      pattern: /^\d+$/,
      defaultPIN: '123456'
    },
    OpenPGP_USER: {
      minLength: 6,
      maxLength: 127,
      pattern: /^.+$/,
      defaultPIN: '123456'
    },
    OpenPGP_ADMIN: {
      minLength: 8,
      maxLength: 127,
      pattern: /^.+$/,
      defaultPIN: '12345678'
    }
  };

  /**
   * Check PIN strength and detect weak PINs
   */
  const checkPINStrength = useCallback((pin, requirements) => {
    // Check for default PINs
    if (pin === requirements.defaultPIN) {
      return {
        strength: 'weak',
        warning: `⚠️ Default ${type} PIN detected! You must change it!`
      };
    }

    // Check for repeating digits
    if (/^(\d)\1+$/.test(pin)) {
      return {
        strength: 'weak',
        warning: '⚠️ Repeating digits are weak (e.g., 111111)'
      };
    }

    // Check for sequential digits
    if (/^(012345|123456|234567|345678|456789|987654|876543|765432|654321|543210)/.test(pin)) {
      return {
        strength: 'weak',
        warning: '⚠️ Sequential digits are weak'
      };
    }

    // Check for common patterns
    const commonPINs = ['123456', '111111', '000000', '123123', '666666', '121212'];
    if (commonPINs.includes(pin)) {
      return {
        strength: 'weak',
        warning: '⚠️ This is a commonly used PIN'
      };
    }

    return {
      strength: 'acceptable',
      warning: null
    };
  }, [type]);

  /**
   * Query remaining PIN retry attempts (PIV)
   * Does NOT consume retry attempts
   */
  const getPIVRetriesLeft = useCallback(async () => {
    try {
      // Select PIV applet
      await dispatch(transceive('00A4040009A00000030800001000'));

      // GET METADATA for PIN (P2=0x80)
      const apdu = '00F7008000';
      const response = await dispatch(transceive(apdu));

      if (response.endsWith('9000')) {
        const data = response.slice(0, -4);
        // Parse TLV response
        // Format: tag [len] value ...
        // We're looking for retries remaining
        let offset = 0;
        while (offset < data.length - 4) {
          const tag = data.substring(offset, offset + 2);
          offset += 2;
          const len = parseInt(data.substring(offset, offset + 2), 16);
          offset += 2;

          if (tag === '03') {
            // Retries remaining tag
            const retries = parseInt(data.substring(offset, offset + 2), 16);
            return retries;
          }

          offset += len * 2;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to get PIN retries:', error);
      return null;
    }
  }, [dispatch]);

  /**
   * Query remaining PIN retry attempts (OpenPGP)
   */
  const getOpenPGPRetriesLeft = useCallback(async (pinType) => {
    try {
      // Select OpenPGP applet
      await dispatch(transceive('00A4040006D27600012401'));

      // GET DATA: PW Status Bytes (TAG 0xC4)
      const apdu = '00CA00C400';
      const response = await dispatch(transceive(apdu));

      if (response.endsWith('9000')) {
        const data = response.slice(0, -4);
        // PW Status format: C4 07 [PW1 retries] [Reset Code retries] [PW3 retries] ...
        if (data.length >= 8) {
          // Skip tag and length
          const pw1Retries = parseInt(data.substring(4, 6), 16);
          const pw3Retries = parseInt(data.substring(8, 10), 16);

          return pinType === 'USER' ? pw1Retries : pw3Retries;
        }
      }

      return null;
    } catch (error) {
      console.error('Failed to get OpenPGP retries:', error);
      return null;
    }
  }, [dispatch]);

  /**
   * Validate PIN format and check retries
   */
  const validatePIN = useCallback(
    debounce(async (pin) => {
      setChecking(true);

      const requirements = type === 'PIV'
        ? PIN_REQUIREMENTS.PIV
        : type === 'OpenPGP_USER'
        ? PIN_REQUIREMENTS.OpenPGP_USER
        : PIN_REQUIREMENTS.OpenPGP_ADMIN;

      // Format validation (instant, no device communication)
      if (pin.length < requirements.minLength) {
        setValidation({
          valid: false,
          error: `PIN must be at least ${requirements.minLength} characters`,
          warning: null,
          retriesLeft: null,
          strength: null
        });
        setChecking(false);
        return;
      }

      if (pin.length > requirements.maxLength) {
        setValidation({
          valid: false,
          error: `PIN must be at most ${requirements.maxLength} characters`,
          warning: null,
          retriesLeft: null,
          strength: null
        });
        setChecking(false);
        return;
      }

      if (!requirements.pattern.test(pin)) {
        setValidation({
          valid: false,
          error: type === 'PIV' ? 'PIN must contain only digits' : 'Invalid PIN format',
          warning: null,
          retriesLeft: null,
          strength: null
        });
        setChecking(false);
        return;
      }

      // Check PIN strength
      const { strength, warning } = checkPINStrength(pin, requirements);

      // Query remaining retries (optional, requires device)
      let retriesLeft = null;
      if (dispatch) {
        try {
          if (type === 'PIV') {
            retriesLeft = await getPIVRetriesLeft();
          } else {
            retriesLeft = await getOpenPGPRetriesLeft(
              type === 'OpenPGP_USER' ? 'USER' : 'ADMIN'
            );
          }
        } catch (error) {
          // Device not connected or error - continue without retries info
          console.warn('Could not fetch retry count:', error);
        }
      }

      // Build validation result
      const newValidation = {
        valid: true,
        error: null,
        warning: warning,
        retriesLeft: retriesLeft,
        strength: strength
      };

      // Add warning for low retries
      if (retriesLeft !== null && retriesLeft <= 2 && retriesLeft > 0) {
        newValidation.warning = `⚠️ Only ${retriesLeft} attempt${retriesLeft === 1 ? '' : 's'} remaining!`;
      }

      // Block if PIN is locked
      if (retriesLeft === 0) {
        newValidation.valid = false;
        newValidation.error = `🔒 PIN is blocked! Use PUK to unblock.`;
      }

      setValidation(newValidation);
      setChecking(false);
    }, 500),
    [type, dispatch, checkPINStrength, getPIVRetriesLeft, getOpenPGPRetriesLeft]
  );

  const handleChange = (e) => {
    const newValue = e.target.value;
    onChange(e);
    validatePIN(newValue);
  };

  // Determine icon
  const getIcon = () => {
    if (checking) {
      return null;
    }

    if (!value || value.length === 0) {
      return null;
    }

    if (validation.error) {
      return <ErrorIcon color="error" />;
    }

    if (validation.warning && validation.strength === 'weak') {
      return <WarningIcon color="warning" />;
    }

    if (validation.valid) {
      return <CheckCircleIcon color="success" />;
    }

    return null;
  };

  // Build helper text
  const getHelperText = () => {
    if (checking) {
      return 'Checking...';
    }

    if (validation.error) {
      return validation.error;
    }

    if (validation.warning) {
      return validation.warning;
    }

    if (validation.retriesLeft !== null) {
      return `${validation.retriesLeft} attempt${validation.retriesLeft === 1 ? '' : 's'} remaining`;
    }

    if (helperText) {
      return helperText;
    }

    const requirements = type === 'PIV'
      ? PIN_REQUIREMENTS.PIV
      : type === 'OpenPGP_USER'
      ? PIN_REQUIREMENTS.OpenPGP_USER
      : PIN_REQUIREMENTS.OpenPGP_ADMIN;

    return `${requirements.minLength}-${requirements.maxLength} ${type === 'PIV' ? 'digits' : 'characters'}`;
  };

  return (
    <Box sx={{ mb: 2 }}>
      <TextField
        {...textFieldProps}
        label={label}
        type="password"
        value={value}
        onChange={handleChange}
        error={!!validation.error}
        helperText={getHelperText()}
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              {getIcon()}
            </InputAdornment>
          )
        }}
      />
      {checking && <LinearProgress sx={{ mt: 1 }} />}
      {validation.strength === 'weak' && (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
          💡 Tip: Use a mix of random digits for better security
        </Typography>
      )}
    </Box>
  );
};

export default PINValidator;

/**
 * Hook for PIN validation (can be used without component)
 */
export const usePINValidation = (type = 'PIV') => {
  const [pin, setPin] = useState('');
  const [isValid, setIsValid] = useState(false);
  const [error, setError] = useState(null);

  const validate = useCallback((value) => {
    const requirements = type === 'PIV'
      ? { minLength: 6, maxLength: 8, pattern: /^\d+$/ }
      : type === 'OpenPGP_USER'
      ? { minLength: 6, maxLength: 127, pattern: /^.+$/ }
      : { minLength: 8, maxLength: 127, pattern: /^.+$/ };

    if (value.length < requirements.minLength) {
      setError(`PIN must be at least ${requirements.minLength} characters`);
      setIsValid(false);
      return false;
    }

    if (value.length > requirements.maxLength) {
      setError(`PIN must be at most ${requirements.maxLength} characters`);
      setIsValid(false);
      return false;
    }

    if (!requirements.pattern.test(value)) {
      setError('Invalid PIN format');
      setIsValid(false);
      return false;
    }

    setError(null);
    setIsValid(true);
    return true;
  }, [type]);

  const handleChange = useCallback((value) => {
    setPin(value);
    validate(value);
  }, [validate]);

  return {
    pin,
    setPin: handleChange,
    isValid,
    error,
    validate
  };
};
